import classNames from "classnames";
import _get from "lodash/get";
import { useTranslation } from "react-i18next";

import { useViewOnly } from "pages/govern/Roles/useViewOnlyContext";

import { MissingPermission } from "../MissingPermissionError/MissingPermission";
import { ROLES_WIZARD_ADD_GROUP_EMPTY } from "../privilegesReducer";
import { PrivilegesState } from "../types";
import { usePrivilegesState } from "../usePrivilegesContext";
import { ALL_SCHEMAS, ALL_TABLES, ALL_VIEWS } from "./constant";

import styles from "./styles.module.scss";

const TableHeader = () => {
  const { t } = useTranslation();
  return (
    <div className={styles.row}>
      <div className={classNames(styles.column, styles.column__objects)}>
        {t("roles_wizard.privileges.column_objects")}
      </div>
      <div className={classNames(styles.column, styles.column__granted)}>
        {t("roles_wizard.privileges.column_granted")}
      </div>
    </div>
  );
};

const nameByType = {
  schema: ALL_SCHEMAS,
  table: ALL_TABLES,
  view: ALL_VIEWS,
};

export const PrivilegesTable = (props: {
  sections: {
    type: string;
    title: string;
    Component: React.ComponentType<any>;
    headerAction: boolean;
  }[];
  catalogName?: string;
}) => {
  const { sections, catalogName } = props;
  const { t } = useTranslation();

  const { privilegesState, privilegesDispatch } = usePrivilegesState();
  const isViewOnly = useViewOnly();

  return (
    <div className={styles.table}>
      <div className={styles.table__inner}>
        <div className={styles.table__header}>
          <TableHeader />
        </div>
        {sections.map(section => {
          const { Component, title, type, headerAction } = section;
          const errors =
            privilegesState.permissionErrors?.[
              type as keyof typeof privilegesState.permissionErrors
            ];
          const sectionGroups = catalogName
            ? privilegesState.databasePrivileges[catalogName]?.[
                type as keyof PrivilegesState
              ] || [{ toAssign: [], toDeny: [], objects: [] }]
            : privilegesState[type as keyof PrivilegesState];
          return (
            <div
              className={styles.resource}
              key={title}
              data-testid={`roles-wizard-section-${type}`}
            >
              <div className={styles.resource__header}>
                {title}
                {!isViewOnly && headerAction && (
                  <div
                    data-testid={`add-new-group-${type}`}
                    className={styles.addGroup}
                    onClick={() => {
                      const action =
                        catalogName &&
                        nameByType[type as keyof typeof nameByType]
                          ? {
                              type: ROLES_WIZARD_ADD_GROUP_EMPTY,
                              resource: type,
                              objects: [
                                {
                                  name: nameByType[
                                    type as keyof typeof nameByType
                                  ],
                                  catalogName,
                                },
                              ],
                              catalogName,
                            }
                          : {
                              type: ROLES_WIZARD_ADD_GROUP_EMPTY,
                              resource: type,
                              objects: [],
                              catalogName,
                            };
                      privilegesDispatch(action);
                    }}
                  >
                    {t("roles_wizard.privileges.button_add_group")}
                  </div>
                )}
              </div>
              <div
                className={styles.sectionGroup}
                data-testid={`privileges-groups-${type}`}
              >
                {sectionGroups.map((_group, index) => {
                  const emptySectionError = _get(privilegesState, [
                    "emptySectionErrors",
                    type,
                    index,
                  ]);
                  return (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                    >
                      <Component
                        index={index}
                        resource={type}
                        catalogName={catalogName}
                      />
                      {emptySectionError && (
                        <div className={styles.emptySectionError}>
                          {emptySectionError}
                        </div>
                      )}
                    </div>
                  );
                })}
                {errors?.length ? (
                  <MissingPermission
                    errors={errors}
                    resource={type}
                    catalogName={catalogName}
                  />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
